/**
 * SubscriptionsPage - 订阅页面
 * 复用 SquarePage 组件，仅展示用户订阅的项目
 *
 * 设计原则：
 * - 广场是主体，订阅只是广场的一个过滤视图
 * - 广场显示更新红点，订阅作为真子集自然显示
 * - 广场不显示红点，订阅作为真子集也不显示
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Spin, Button } from 'antd';
import { SquarePage, type PostListRenderProps, type PostDetailModalRenderProps } from './SquarePage';
import { CustomEmpty } from './CustomEmpty';
import { getMySubscriptions } from '../apiClient';
import ipEmpty2Image from '../assets/images/ip-empty-2.png';

const { Content } = Layout;

// 重导出类型以保持向后兼容
export type { PostListRenderProps, PostDetailModalRenderProps };

export interface SubscriptionsPageProps {
  /** 当前用户 ID */
  userId?: string;
  /** 是否已登录 */
  isLoggedIn: boolean;
  /** 是否正在初始化（检查登录状态） */
  isInitializing?: boolean;
  /** URL 中的 itemId 参数 */
  urlItemId?: string;
  /** 需要高亮的帖子 ID（从通知跳转） */
  highlightPostId?: string | null;
  /** 导航函数 */
  navigate: (path: string, options?: any) => void;
  /** 渲染帖子详情弹窗（可选） */
  renderPostDetailModal?: (props: PostDetailModalRenderProps) => React.ReactNode;
  /** 渲染帖子列表（可选） */
  renderPostList?: (props: PostListRenderProps) => React.ReactNode;
  /** 剪藏到本地弹窗（Electron 专用，已废弃） */
  renderClipToLocalModal?: () => React.ReactNode;
}

export const SubscriptionsPage: React.FC<SubscriptionsPageProps> = ({
  userId,
  isLoggedIn,
  isInitializing = false,
  urlItemId,
  highlightPostId,
  navigate,
  renderPostDetailModal,
  renderPostList,
}) => {
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [error, setError] = useState<Error | null>(null);

  // 加载订阅数据
  useEffect(() => {
    if (isInitializing) return;

    if (!isLoggedIn) {
      setLoading(false);
      return;
    }

    const loadSubscriptions = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await getMySubscriptions();
        const subscriptionsData = (result as any)?.subscriptions || result || [];
        setSubscriptions(Array.isArray(subscriptionsData) ? subscriptionsData : []);
      } catch (err) {
        console.error('Failed to load subscriptions:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    loadSubscriptions();
  }, [isLoggedIn, isInitializing]);

  // 提取订阅项的 ID 集合
  const subscribedItemIds = useMemo(() => {
    const ids = new Set<string>();
    subscriptions.forEach(sub => {
      // 支持旧版和新版数据结构
      if (sub.sharedTagGroupId) ids.add(sub.sharedTagGroupId);
      if (sub.sharedTagId) ids.add(sub.sharedTagId);
      if (sub.cloudTagGroupId) ids.add(sub.cloudTagGroupId);
      if (sub.cloudTagId) ids.add(sub.cloudTagId);
      // 兼容已转换的格式
      if (sub.tagGroup?.id) ids.add(sub.tagGroup.id);
      if (sub.tag?.id) ids.add(sub.tag.id);
    });
    return ids;
  }, [subscriptions]);

  // 未登录状态（等待初始化完成后再显示）
  if (!isInitializing && !isLoggedIn) {
    return (
      <Content
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 16,
          padding: 48,
        }}
      >
        <CustomEmpty image={ipEmpty2Image} description="请先登录以查看您的订阅" />
        <Button type="primary" onClick={() => navigate('/auth/login?redirect=/subscriptions')}>
          登录
        </Button>
      </Content>
    );
  }

  // 加载中 - 显示侧边栏框架 + 内容区 loading
  if (loading || isInitializing) {
    return (
      <>
        {/* 二级侧栏框架 */}
        <Layout.Sider
          width={288}
          style={{
            borderRight: '1px solid var(--custom-border-secondary)',
            padding: '12px',
          }}
        >
          <div style={{ padding: '18px', marginBottom: '0px' }}>
            <h2 style={{
              margin: 0,
              marginBottom: '8px',
              fontWeight: 600,
              color: 'var(--ant-color-text)',
            }}>
              我的订阅
            </h2>
            <p style={{ fontSize: '12px', lineHeight: '20px', color: 'var(--ant-color-text-secondary)', margin: 0 }}>
              查看你订阅的收藏夹
            </p>
          </div>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
          </div>
        </Layout.Sider>
        {/* 内容区 loading */}
        <Content style={{ padding: 0, position: 'relative' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
          }}>
            <Spin size="large" />
          </div>
        </Content>
      </>
    );
  }

  // 错误状态
  if (error) {
    return (
      <Content style={{ padding: 0, position: 'relative' }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: '64px 20px',
        }}>
          <CustomEmpty
            description="加载订阅失败，请刷新重试"
            style={{ marginTop: '100px' }}
          />
        </div>
      </Content>
    );
  }

  // 无订阅
  if (subscribedItemIds.size === 0) {
    return (
      <Content style={{ padding: 0, position: 'relative' }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: '64px 20px',
        }}>
          <CustomEmpty
            image={ipEmpty2Image}
            description="暂无订阅，去广场看看吧"
            style={{ marginTop: '100px' }}
          />
          <a
            onClick={() => navigate('/square')}
            style={{
              marginTop: 16,
              color: 'var(--ant-color-primary)',
              cursor: 'pointer',
            }}
          >
            前往广场
          </a>
        </div>
      </Content>
    );
  }

  // 有订阅，复用 SquarePage
  return (
    <SquarePage
      userId={userId}
      isLoggedIn={isLoggedIn}
      urlItemId={urlItemId}
      highlightPostId={highlightPostId}
      navigate={navigate}
      mode="subscriptions"
      subscribedItemIds={subscribedItemIds}
      renderPostDetailModal={renderPostDetailModal}
      renderPostList={renderPostList}
    />
  );
};

export default SubscriptionsPage;
